(() => {
  const SEARCH_PAGE_SELECTOR = '[data-kl-search-page]';
  const SUGGESTIONS_SELECTOR = '[data-kl-search-suggestions]';
  const SUGGESTIONS_LIST_SELECTOR = '[data-kl-search-suggestions-list]';
  const INPUT_SELECTOR = '[data-kl-search-page-input]';
  const CLEAR_SELECTOR = '[data-kl-search-page-clear]';
  const FORM_SELECTOR = '[data-kl-search-page-form]';

  const MINIMUM_QUERY_LENGTH = 2;

  /*
    The page is asked for each typed word in quotes, so a product has to carry every one of
    them. Unquoted, Shopify's storefront search matches a single word across every indexed
    field including the description — "mahjong" came back as ~1000 of 2411 products where
    only 12 carry the word. options[fields] is ignored for product results and AND is read
    as a term rather than an operator, so quoting is the only lever, and word by word rather
    than one quoted phrase because a phrase is matched in the order it was typed.
    snippets/kl-search-query.liquid does the same thing in Liquid, for the links the overlay
    and the suggestions render.

    The field itself shows the words without the quotes, and snippets/kl-search-terms.liquid
    takes them off server-side so they are never painted. All that is left here is putting
    them back on the way out.
  */
  function normalizeTerm(queryValue) {
    return queryValue.trim().replace(/\s+/g, ' ');
  }

  function buildSearchQuery(term) {
    return term
      .split(' ')
      .map((searchWord) => `"${searchWord}"`)
      .join(' ');
  }

  function getSuggestionsUrl({ endpoint, baseUrl, query, limit, sectionId }) {
    const suggestionsUrl = new URL(endpoint, baseUrl);

    suggestionsUrl.searchParams.set('q', query);
    suggestionsUrl.searchParams.set('resources[type]', 'collection');
    suggestionsUrl.searchParams.set('resources[limit]', String(limit));
    suggestionsUrl.searchParams.set('section_id', sectionId);

    return suggestionsUrl.toString();
  }

  function getIsQuerySearchable(queryValue) {
    return queryValue.trim().length >= MINIMUM_QUERY_LENGTH;
  }

  function readSuggestionsList(responseText) {
    const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');

    return responseDocument.querySelector(SUGGESTIONS_LIST_SELECTOR);
  }

  async function loadSuggestions(rowElement) {
    const {
      klSearchSuggestionsTerms: terms,
      klSearchSuggestionsLimit: limit,
      klSearchSuggestionsSection: sectionId,
      klSearchSuggestionsUrl: endpoint,
    } = rowElement.dataset;

    if (!terms || !sectionId || !endpoint) return;
    if (!getIsQuerySearchable(terms)) return;

    try {
      const response = await fetch(
        getSuggestionsUrl({
          endpoint,
          baseUrl: rowElement.baseURI,
          query: normalizeTerm(terms),
          limit: Number.parseInt(limit, 10) || 4,
          sectionId,
        })
      );

      if (!response.ok) throw new Error(`Shopify answered ${response.status}`);

      const listElement = readSuggestionsList(await response.text());

      if (!listElement) return;

      rowElement.querySelector(SUGGESTIONS_LIST_SELECTOR)?.remove();
      rowElement.append(listElement);
      rowElement.hidden = false;
    } catch (error) {
      console.debug('kl-search-results: could not load suggested collections', { terms, error });
    }
  }

  function initClear(clearElement, inputElement) {
    clearElement.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();

      inputElement.value = '';
      clearElement.hidden = true;
      inputElement.focus();
    });

    inputElement.addEventListener('input', () => {
      clearElement.hidden = inputElement.value.length === 0;
    });
  }

  /*
    The quotes go on the URL rather than into the field, so the shopper never sees them.
    Writing them into the input and letting the form submit natively did show them, for
    however long the browser took to leave the page.

    Navigating by hand is what that costs. Without JavaScript the form submits itself and
    asks for the bare words, which is the behaviour this page had before any of this.
  */
  function initQueryField(formElement, inputElement) {
    formElement.addEventListener('submit', (event) => {
      const term = normalizeTerm(inputElement.value);

      if (term.length === 0) return;

      event.preventDefault();

      /* From the form, so the hidden options[...] fields it declares are carried. Written
         by hand, the URL dropped options[unavailable_products] the moment this stopped
         being a native submit, leaving that choice to a Search & Discovery setting. */
      const parameters = new URLSearchParams(new FormData(formElement));

      parameters.set('q', buildSearchQuery(term));

      location.assign(`${formElement.getAttribute('action')}?${parameters}`);
    });
  }

  function initSearchPage(pageElement) {
    const rowElement = pageElement.querySelector(SUGGESTIONS_SELECTOR);
    const inputElement = pageElement.querySelector(INPUT_SELECTOR);
    const clearElement = pageElement.querySelector(CLEAR_SELECTOR);
    const formElement = pageElement.querySelector(FORM_SELECTOR);

    if (rowElement) loadSuggestions(rowElement);

    if (formElement && inputElement) initQueryField(formElement, inputElement);

    if (inputElement && clearElement) initClear(clearElement, inputElement);
  }

  document.querySelectorAll(SEARCH_PAGE_SELECTOR).forEach(initSearchPage);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(SEARCH_PAGE_SELECTOR).forEach(initSearchPage);
  });
})();
